import { useCallback, useEffect, useState } from 'react';
import {
  deleteOwnershipSharePeriod,
  getOwnershipShareHistory,
  updateOwnershipShares,
  type ClubMemberRole,
  type OwnershipSharePeriod,
} from '../../../api';
import { User } from '../../../types';
import { canHaveOwnershipShare } from '../../../utils/isGeneralUser';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  formatOwnershipShare,
  formatOwnershipShareSum,
  isOwnershipShareSumComplete,
  parseOwnershipShareInput,
} from '../../../utils/ownershipShare';
import { isPlausibleIsoDate, todayIso } from '../membersUtils';

export function useOwnershipShares({
  members,
  loadMembers,
  sharesAircraftId,
  setSharesAircraftId,
  currentUserId,
  showError,
  showSuccess,
  patchUser,
}: {
  members: User[];
  loadMembers: (forAircraftId?: number) => Promise<User[]>;
  sharesAircraftId: number;
  setSharesAircraftId: (next: number) => void;
  currentUserId: number | null | undefined;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  patchUser: (patch: { is_pilot?: boolean }) => void;
}) {
  const [sharesModalOpen, setSharesModalOpen] = useState(false);
  const [sharesModalMemberIds, setSharesModalMemberIds] = useState<number[]>(
    []
  );
  const [sharesLeaveUserId, setSharesLeaveUserId] = useState<number | null>(
    null
  );
  const [sharesRestoreRole, setSharesRestoreRole] = useState<
    'pilot' | 'admin' | null
  >(null);
  const [sharesRoleChange, setSharesRoleChange] = useState<{
    userId: number;
    toRole: ClubMemberRole;
  } | null>(null);
  /** null = not owner is_pilot toggle flow */
  const [sharesOwnerIsPilot, setSharesOwnerIsPilot] = useState<boolean | null>(
    null
  );
  const [sharesAircraftLocked, setSharesAircraftLocked] = useState(false);
  const [sharesEffectiveFrom, setSharesEffectiveFrom] = useState(todayIso);
  const [shareDrafts, setShareDrafts] = useState<Record<number, string>>({});
  const [shareDraftsByAircraft, setShareDraftsByAircraft] = useState<
    Record<number, Record<number, string>>
  >({});
  const [sharesSaving, setSharesSaving] = useState(false);
  const [shareHistory, setShareHistory] = useState<OwnershipSharePeriod[]>(
    []
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState<string | null>(null);

  const loadShareHistory = useCallback(
    async (forAircraftId: number) => {
      if (forAircraftId <= 0) {
        setShareHistory([]);
        return;
      }
      setHistoryLoading(true);
      try {
        const res = await getOwnershipShareHistory(forAircraftId);
        setShareHistory(res.data.periods || []);
      } catch (error) {
        setShareHistory([]);
        showError(
          getApiErrorMessage(error, 'Ошибка загрузки истории долей')
        );
      }
      setHistoryLoading(false);
    },
    [showError]
  );

  useEffect(() => {
    if (!sharesModalOpen) return;
    void loadShareHistory(sharesAircraftId);
  }, [sharesModalOpen, sharesAircraftId, loadShareHistory]);

  const openSharesModal = (
    list: User[] = members,
    activatingIds: number[] = [],
    leaveUserId: number | null = null,
    restoreUserId: number | null = null,
    restoreAs: 'pilot' | 'admin' | null = null,
    roleChange: { userId: number; toRole: ClubMemberRole } | null = null,
    ownerIsPilot: boolean | null = null
  ) => {
    setSharesAircraftLocked(false);
    const roleChangeMember = roleChange
      ? list.find((m) => m.id === roleChange.userId)
      : undefined;
    const demoting =
      roleChange &&
      canHaveOwnershipShare(
        roleChangeMember?.role,
        roleChangeMember?.is_pilot
      ) &&
      !canHaveOwnershipShare(roleChange.toRole)
        ? roleChange
        : null;
    const promoting =
      roleChange &&
      !canHaveOwnershipShare(
        roleChangeMember?.role,
        roleChangeMember?.is_pilot
      ) &&
      canHaveOwnershipShare(roleChange.toRole)
        ? roleChange
        : null;

    const ownerLeaving =
      ownerIsPilot === false && currentUserId != null
        ? list.find((m) => m.id === currentUserId && m.role === 'owner')
        : undefined;
    const ownerJoining =
      ownerIsPilot === true && currentUserId != null
        ? list.find((m) => m.id === currentUserId && m.role === 'owner')
        : undefined;

    const effectiveLeaveId = demoting
      ? demoting.userId
      : ownerLeaving
        ? ownerLeaving.id
        : leaveUserId;
    const leaving = effectiveLeaveId
      ? list.find((m) => m.id === effectiveLeaveId)
      : undefined;
    const restoring = restoreUserId
      ? list.find((m) => m.id === restoreUserId && m.role === 'inactive')
      : undefined;
    const promotingMember = promoting
      ? list.find((m) => m.id === promoting.userId)
      : ownerJoining;
    const freed =
      leaving &&
      leaving.ownership_share != null &&
      Number.isFinite(Number(leaving.ownership_share))
        ? Number(leaving.ownership_share)
        : 0;

    const ownersList = list.filter(
      (m) =>
        canHaveOwnershipShare(m.role, m.is_pilot) &&
        m.id !== effectiveLeaveId
    );
    const activating = list.filter(
      (m) => m.role === 'pending' && activatingIds.includes(m.id)
    );
    const modalMembers = [
      ...ownersList,
      ...activating,
      ...(restoring ? [restoring] : []),
      ...(promotingMember ? [promotingMember] : []),
    ];
    const drafts: Record<number, string> = {};

    if (effectiveLeaveId != null && ownersList.length > 0) {
      const n = ownersList.length;
      const baseExtra = Math.round((freed / n) * 1e6) / 1e6;
      let allocated = 0;
      ownersList.forEach((m, i) => {
        const old =
          m.ownership_share != null &&
          Number.isFinite(Number(m.ownership_share))
            ? Number(m.ownership_share)
            : 0;
        const extra =
          i === n - 1
            ? Math.round((freed - allocated) * 1e6) / 1e6
            : baseExtra;
        if (i < n - 1) allocated += baseExtra;
        drafts[m.id] = formatOwnershipShare(
          Math.round((old + extra) * 1e6) / 1e6
        );
      });
    } else if (restoring || promotingMember || activating.length > 0) {
      const newcomers = [
        ...activating,
        ...(restoring ? [restoring] : []),
        ...(promotingMember ? [promotingMember] : []),
      ];
      const n = ownersList.length + newcomers.length;
      const factor = n > 0 ? (n - newcomers.length) / n : 0;
      const scaledVals = ownersList.map((m) => {
        const old =
          m.ownership_share != null &&
          Number.isFinite(Number(m.ownership_share))
            ? Number(m.ownership_share)
            : 0;
        return Math.round(old * factor * 1e6) / 1e6;
      });
      if (scaledVals.length > 1) {
        const targetOthers = Math.round(
          (1 - (1 / n) * newcomers.length) * 1e6
        ) / 1e6;
        const head = scaledVals.slice(0, -1).reduce((a, b) => a + b, 0);
        scaledVals[scaledVals.length - 1] = Math.round(
          (targetOthers - head) * 1e6
        ) / 1e6;
      }
      ownersList.forEach((m, i) => {
        drafts[m.id] = formatOwnershipShare(scaledVals[i] ?? 0);
      });
      const ownersSum = scaledVals.reduce((a, b) => a + b, 0);
      const forNew = Math.round((1 - ownersSum) * 1e6) / 1e6;
      const mNew = newcomers.length;
      const baseNew = Math.round((forNew / mNew) * 1e6) / 1e6;
      newcomers.forEach((m, i) => {
        drafts[m.id] = formatOwnershipShare(
          i === mNew - 1
            ? Math.round((forNew - baseNew * (mNew - 1)) * 1e6) / 1e6
            : baseNew
        );
      });
    } else {
      modalMembers.forEach((m) => {
        drafts[m.id] =
          m.ownership_share == null ||
          !Number.isFinite(Number(m.ownership_share))
            ? ''
            : formatOwnershipShare(Number(m.ownership_share));
      });
    }

    setSharesLeaveUserId(demoting || ownerLeaving ? null : leaveUserId);
    setSharesRestoreRole(
      restoreUserId != null ? restoreAs || 'pilot' : null
    );
    setSharesRoleChange(roleChange);
    setSharesOwnerIsPilot(ownerIsPilot);
    const fromDates = list
      .map((m) => m.ownership_share_from)
      .filter((d): d is string => Boolean(d));
    setSharesEffectiveFrom(fromDates[0] || todayIso());
    setSharesModalMemberIds(modalMembers.map((m) => m.id));
    setShareDrafts(drafts);
    setShareDraftsByAircraft((prev) => ({
      ...prev,
      [sharesAircraftId]: drafts,
    }));
    setSharesModalOpen(true);
  };

  const openSharesForAircraft = async (forAircraftId: number) => {
    if (forAircraftId <= 0) {
      showError('Выберите борт для долей владения');
      return;
    }
    setSharesAircraftLocked(true);
    setSharesAircraftId(forAircraftId);
    const list = await loadMembers(forAircraftId);
    openSharesModal(list);
    setSharesAircraftLocked(true);
  };

  const closeSharesModal = () => {
    if (sharesSaving) return;
    setSharesModalOpen(false);
    setSharesModalMemberIds([]);
    setSharesLeaveUserId(null);
    setSharesRestoreRole(null);
    setSharesRoleChange(null);
    setSharesOwnerIsPilot(null);
    setSharesAircraftLocked(false);
    setShareDrafts({});
    setShareDraftsByAircraft({});
  };

  const saveOwnershipShares = async () => {
    const modalMembers = members.filter((m) =>
      sharesModalMemberIds.includes(m.id)
    );
    const payload: { user_id: number; ownership_share: number }[] = [];
    for (const m of modalMembers) {
      const raw = shareDrafts[m.id] ?? '';
      const parsed = parseOwnershipShareInput(raw);
      if (parsed === 'invalid' || parsed == null) {
        showError(`Доля «${m.name}»: укажите дробь, например 1/5`);
        return;
      }
      payload.push({ user_id: m.id, ownership_share: parsed });
    }
    const sum = payload.reduce((acc, s) => acc + s.ownership_share, 0);
    const skipSumCheck =
      payload.length === 0 && sharesOwnerIsPilot === false;
    if (!skipSumCheck && !isOwnershipShareSumComplete(sum)) {
      showError(
        `Сумма долей должна быть равна 1 (сейчас ${formatOwnershipShareSum(sum)})`
      );
      return;
    }
    if (!isPlausibleIsoDate(sharesEffectiveFrom)) {
      showError('Укажите корректную дату «Доли действуют с»');
      return;
    }
    const latestFrom =
      shareHistory.length > 0
        ? shareHistory[shareHistory.length - 1].effective_from
        : null;
    if (latestFrom && sharesEffectiveFrom < latestFrom) {
      showError(
        'Нельзя задать дату раньше текущего периода долей. Оставьте дату текущего снимка, поставьте позже или удалите прошлый период, если он ошибочный'
      );
      return;
    }

    if (sharesAircraftId <= 0) {
      showError('Выберите борт для долей владения');
      return;
    }

    const ownerToggle = sharesOwnerIsPilot;
    const aircraftShares = Object.entries(shareDraftsByAircraft)
      .filter(([id]) => Number(id) !== sharesAircraftId)
      .map(([id, drafts]) => {
        const extra = Object.entries(drafts).map(([uid, raw]) => ({
          user_id: Number(uid),
          ownership_share: parseOwnershipShareInput(raw),
        }));
        return {
          aircraft_id: Number(id),
          extra,
        };
      })
      .filter((item) =>
        item.extra.every(
          (row) => row.ownership_share !== 'invalid' && row.ownership_share != null
        )
      )
      .map((item) => ({
        aircraft_id: item.aircraft_id,
        shares: item.extra.map((row) => ({
          user_id: row.user_id,
          ownership_share: Number(row.ownership_share),
        })),
        effective_from: sharesEffectiveFrom,
      }))
      .filter((item) => isOwnershipShareSumComplete(
        item.shares.reduce((acc, s) => acc + s.ownership_share, 0)
      ));
    setSharesSaving(true);
    try {
      await updateOwnershipShares({
        aircraft_id: sharesAircraftId,
        shares: payload,
        effective_from: sharesEffectiveFrom,
        ...(sharesLeaveUserId != null
          ? { deactivate_user_id: sharesLeaveUserId }
          : {}),
        ...(sharesRestoreRole != null
          ? { restore_role: sharesRestoreRole }
          : {}),
        ...(sharesRoleChange != null
          ? {
              role_change_user_id: sharesRoleChange.userId,
              role_change_to: sharesRoleChange.toRole,
            }
          : {}),
        ...(ownerToggle != null ? { owner_is_pilot: ownerToggle } : {}),
        ...(aircraftShares.length > 0 ? { aircraft_shares: aircraftShares } : {}),
      });
      const left = sharesLeaveUserId != null;
      const roleChanged = sharesRoleChange != null;
      const activated = modalMembers.some(
        (m) => m.role === 'pending' || m.role === 'inactive'
      );
      showSuccess(
        ownerToggle != null
          ? 'Статус собственника обновлён'
          : left
            ? 'Участник удалён, доли обновлены'
            : roleChanged
              ? 'Роль изменена, доли обновлены'
              : activated
                ? 'Участник активирован, доли сохранены'
                : 'Доли владения обновлены'
      );
      setSharesModalOpen(false);
      setSharesModalMemberIds([]);
      setSharesLeaveUserId(null);
      setSharesRestoreRole(null);
      setSharesRoleChange(null);
      setSharesOwnerIsPilot(null);
      setShareDrafts({});
      setShareDraftsByAircraft({});
      if (ownerToggle != null) {
        patchUser({ is_pilot: ownerToggle });
      }
      await loadMembers();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка доли владения'));
    }
    setSharesSaving(false);
  };

  const confirmDeleteSharePeriod = async () => {
    if (!periodToDelete || sharesAircraftId <= 0) return;
    setSharesSaving(true);
    try {
      await deleteOwnershipSharePeriod(sharesAircraftId, periodToDelete);
      setPeriodToDelete(null);
      showSuccess('Период долей удалён');
      await loadShareHistory(sharesAircraftId);
      await loadMembers(sharesAircraftId);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось удалить период'));
    }
    setSharesSaving(false);
  };

  return {
    sharesModalOpen,
    sharesModalMemberIds,
    setSharesModalMemberIds,
    sharesLeaveUserId,
    sharesRestoreRole,
    sharesRoleChange,
    sharesOwnerIsPilot,
    sharesAircraftLocked,
    sharesEffectiveFrom,
    setSharesEffectiveFrom,
    shareDrafts,
    setShareDrafts,
    shareDraftsByAircraft,
    setShareDraftsByAircraft,
    sharesSaving,
    shareHistory,
    historyLoading,
    periodToDelete,
    setPeriodToDelete,
    openSharesModal,
    openSharesForAircraft,
    closeSharesModal,
    saveOwnershipShares,
    confirmDeleteSharePeriod,
  };
}
